import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import {
  MatDatepicker,
  MatDatepickerInputEvent,
} from '@angular/material/datepicker';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { createMask, InputmaskOptions } from '@ngneat/input-mask';

dayjs.extend(customParseFormat);

@Component({
  selector: 'ui-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatePickerComponent implements OnInit, AfterViewInit {
  @Input() public inputId = '';
  @Input() public label = '';
  @Input() public autocomplete = 'on';
  @Input() public control: FormControl<Date | null> =
    new FormControl<Date | null>(null);
  @Input() public hint?: string;
  @Input() public isDisabled = false;

  @ViewChild(MatDatepicker)
  public datePicker!: MatDatepicker<Date>;

  private _wasRequired = false;

  public dateInputMask!: InputmaskOptions<Date>;
  public format!: 'DD/MM/YYYY' | 'MM/YYYY';

  @Input()
  public set dateFormat(format: 'DD/MM/YYYY' | 'MM/YYYY') {
    this.format = format;
    this._setMask();
  }

  public ngOnInit(): void {
    if (this.control.hasValidator(Validators.required)) {
      this._wasRequired = true;
    }

    if (!this.dateInputMask) {
      this._setMask();
    }

    if (this.isDisabled) {
      this.control.disable();
    }
  }

  public ngAfterViewInit(): void {
    if (this._wasRequired) {
      // NOTE: workaround for @ngneat/input-mask composing validators [rga@23/01/2023]
      this.control.addValidators(Validators.required);
    }

    if (this.control.value) {
      this.datePicker.select(this.control.value);
    }
  }

  public onControlTouched(): void {
    this.control.markAsTouched();
  }

  public onDateChangedViaDatePicker({
    target: { value },
  }: MatDatepickerInputEvent<unknown, unknown | null>): void {
    this.onControlTouched();
    this._setAndDisplayValue(value as Date | null, false);
  }

  public onMonthChanged(value: Date | null): void {
    if (this.format === 'MM/YYYY') {
      this.datePicker.close();
      this._setAndDisplayValue(value);
    }
  }

  public onValueChanged(event: Event): void {
    const dateString = (event.target as HTMLInputElement).value;
    if (dateString) {
      this._setAndDisplayValue(dayjs(dateString, this.format).toDate());
      return;
    }
    this._setAndDisplayValue(null);
  }

  private _setAndDisplayValue(
    value: Date | null,
    updatePicker: boolean = true,
  ): void {
    const day = dayjs(value);
    if (this.control.value === value) {
      return;
    }
    if (day.isValid()) {
      this.control.patchValue(value);
      if (updatePicker) this.datePicker.select(value as Date);
    }
  }

  private _setMask(): void {
    this.format = this.format ?? 'DD/MM/YYYY';
    this.dateInputMask = createMask<Date>({
      alias: 'datetime',
      placeholder: '_',
      showMaskOnFocus: true,
      inputFormat: this.format.toLowerCase(),
      parser: (value: string): Date => {
        const values = value.split('/');
        if (this.format === 'MM/YYYY') {
          return new Date(+values[1], +values[0] - 1, 1);
        }
        return new Date(+values[2], +values[1] - 1, +values[0]);
      },
    });
  }
}
